import { Inject, Injectable } from '@angular/core';

import { BetGroupType, BetSlipType, EdsPromoTokenType, KeyValueInformation, OddsBoostFormula, OddsFormat, PromotionTokenType } from '@bpos';
import { BetType } from '@bpos/common';
import { ErrorIdentifierType } from '@bpos/common/bet-placement';
import { PromoTokenType, SignPost, SignPostType } from '@bpos/v1/my-bets';
import { BetSlip, PlaceBetRequest } from '@bpos/v2/bet-placement';
import { AccountBalanceLoggerService } from '@frontend/sports/account-balance/feature';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { Fraction, Odds, OddsOperations } from '@frontend/sports/odds/feature';
import {
    FailedBetslip,
    PlaceBetResponse,
    PlaceBetStatus,
    PlacedBetslip,
    PlacementErrorType,
    PlacementType,
    ResponseError,
    SlipOptionType,
    SucceededBetslip,
} from '@frontend/sports/types/betslip';
import { Decimal } from 'decimal.js';

import { TokensCalculatorService } from '../../../../lib/tokens-base/tokens-calculator.service';
import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { IBetslipState } from '../../../base/store/state';
import { getPosError } from '../../../base/utils/pos-errors';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV2StandardPick } from '../../../core/picks/betslip-v2-standard-pick';
import { V2OptionMarketPickId, pickIdFactory } from '../../../core/picks/pick-id';
import { BetslipV2OptionMarketXCastRacePick } from '../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { getLegsCount } from '../../../core/utils';
import { RewardTokenAdditionalInfoKeys } from '../../reward-tokens/reward-tokens.model';
import { reconvertSystem } from '../../system-bet/utils/system-type.helper';
import { BetPlacementError } from '../../validation/errors/bet-placement-error';
import { ResultError } from '../../validation/errors/result/result-error';
import {
    IBetPicks,
    IFailedSlipPickResult,
    IFailedSlipResult,
    IPlacedBetslipResult,
    ISucceededPartialSlipResult,
    ISucceededSlipPickResult,
    ISucceededSlipResult,
    SlipResultStatus,
} from '../models';
import { PlaceBetErrorMapper } from './place-bet-error-mapper';
import { getFailedSlipDetails } from './place-bet-response.utils';

@Injectable()
export default class PlaceBetResponseProcessorService {
    private readonly messages: {
        freeBetAccepted: string;
        editBetSuccessful: string;
        singleAccepted: string;
        singleBetsAccepted: string;
        multiAccepted: string;
        systemBetsAccepted: string;
        teaserBetAccepted: string;
    };

    constructor(
        private errorMapper: PlaceBetErrorMapper,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messagesProvider: IBetslipMessagesProviderService,
        private stringInterpolationPipe: StringInterpolationPipe,
        private tokensCalculator: TokensCalculatorService,
        private balanceLogger: AccountBalanceLoggerService,
    ) {
        const messages = messagesProvider.getMessages();
        this.messages = {
            freeBetAccepted: messages.crmTokens.confirmation.YourBetHasBeenPlaced,
            editBetSuccessful: messages.editBet.editBetSuccessful,
            singleAccepted: messages.common.SingleAccepted,
            singleBetsAccepted: messages.common.SingleBetsAccepted,
            multiAccepted: messages.common.MultiAccepted,
            systemBetsAccepted: messages.common.SystemBetsAccepted,
            teaserBetAccepted: messages.teaserBet?.messages.TeaserPlaced!,
        };
    }

    getStatus(response: PlaceBetResponse): PlaceBetStatus {
        if (!response.betslips) {
            return PlaceBetStatus.Failed;
        }

        return response.status;
    }

    processPending(response: PlaceBetResponse): { delay: number } {
        return {
            delay: response.pendingStatus?.delay || 400,
        };
    }

    processDone(
        response: PlaceBetResponse,
        context: { request: PlaceBetRequest; requestPicks: IBetPicks; state: IBetslipState },
    ): IPlacedBetslipResult {
        return {
            status: response.status,
            resultMessage: this.getMessage(response, context.state),
            slips: response.betslips.map((betslip, i) => {
                if (betslip.status === PlacementType.Succeeded) {
                    this.balanceLogger.setBalanceInfoPostSuccessfulBetPlacement();

                    return this.mapSucceededSlip(betslip, context.request, context.requestPicks);
                } else {
                    const selectedType =
                        context.state.types.base.currentSelectedType === BetslipType.EditBet ||
                        context.state.types.base.currentSelectedType === BetslipType.Teaser
                            ? context.state.types.base.currentSelectedType
                            : this.mapBetslipType(context.request.betSlips[i].betSlipType);

                    return this.mapFailedSlip(context.request.requestId, betslip, context.requestPicks, context.request.betSlips[i], selectedType);
                }
            }),
        };
    }

    private processErrors(
        failedSlip: FailedBetslip | undefined,
        requestPicks: IBetPicks,
        betSlipType: BetslipType,
        systemType: string,
    ): { slipErrors: BetPlacementError[]; picks: { [pickId: string]: IFailedSlipPickResult } } {
        const res: { slipErrors: BetPlacementError[]; picks: { [pickId: string]: IFailedSlipPickResult } } = {
            slipErrors: [],
            picks: {},
        };

        // in case of multiple limit exceeded errors we are taking only, with lowest newStakeHint
        const bposErrors = this.reduceLimitExceededErrors(failedSlip?.errors ?? []);

        for (const error of bposErrors) {
            // error.index can be undefined despite not being marked as such
            // for multi singles there are certain errors we need to store on a pick level even if error.index is undefined
            const betIndex = error.index || failedSlip?.index;

            if (betIndex && !this.isBetslipErrorType(error) && (requestPicks.picks[betIndex] || requestPicks.groups[betIndex])) {
                let pick: BetslipPick;
                if (error.indexRelevantTo === ErrorIdentifierType.BetGroup && !!requestPicks.groups) {
                    pick = requestPicks.groups[betIndex];
                } else {
                    pick = requestPicks.picks[betIndex];
                }
                const resultError = this.errorMapper.map(error, pick) as ResultError;

                if (!pick) {
                    res.slipErrors.push(resultError);
                } else {
                    if (res.picks[pick.id.toString()]) {
                        res.picks[pick.id.toString()].errors.push(resultError);
                    } else {
                        res.picks[pick.id.toString()] = {
                            index: betIndex,
                            clientPick: pick,
                            errors: [resultError],
                        };
                    }
                }
            } else if (betIndex) {
                const pickId = requestPicks.picks[betIndex]?.id;
                const betPlacementError = this.errorMapper.map(error, null);
                betPlacementError.slipId = this.errorMapper.getSlipId(betSlipType, pickId, systemType);
                res.slipErrors.push(betPlacementError);
            } else {
                const betPlacementError = this.errorMapper.map(error, null);
                res.slipErrors.push(betPlacementError);
            }
        }

        return res;
    }

    private reduceLimitExceededErrors(errors: ResponseError[]): ResponseError[] {
        const newStakeHintComparer = (a: ResponseError, b: ResponseError) => a.newStakeHint! - b.newStakeHint!;
        const isLimitExceededError = (error: ResponseError) =>
            error.hasOwnProperty('newStakeHint') &&
            error?.type !== PlacementErrorType.OptionInvisible &&
            error?.type !== PlacementErrorType.OddsChanged;

        const lowestNewStakeHintError = errors.filter(isLimitExceededError).sort(newStakeHintComparer)[0];

        return errors.filter((error) => error === lowestNewStakeHintError || !isLimitExceededError(error));
    }

    private isBetslipErrorType(error: ResponseError): boolean {
        // We need to check the range value here because we have no implementation for this yet in betslip-v3
        return getPosError(error).range === 'betslip';
    }

    private getMessage(response: PlaceBetResponse, context: IBetslipState): string {
        if (response.status === PlaceBetStatus.Success) {
            const isFreeBet = response.betslips.some((b) => b.succeeded?.isFreeBet);
            if (isFreeBet) {
                return this.messages.freeBetAccepted;
            }
            if (context.types.base.currentSelectedType === BetslipType.EditBet) {
                // Temporary check only for edit bet until we migrate betslip to ngrx
                return this.messages.editBetSuccessful;
            }

            if (context.types.base.currentSelectedType === BetslipType.Teaser) {
                return this.messages.teaserBetAccepted;
            }

            // If total status is success there should be at least one
            const firstSucceededSlipResult = response.betslips.filter((b) => b.status === PlacementType.Succeeded)[0].succeeded!;
            switch (firstSucceededSlipResult.betType) {
                case BetSlipType.Combo:
                    if (firstSucceededSlipResult.betGroups?.some((bg) => bg.betGroupType === BetGroupType.Teaser)) {
                        return this.messages.teaserBetAccepted;
                    }

                    return this.messages.multiAccepted;
                case BetSlipType.Single:
                    // if we placed from Combo we want to have the success message in-sync(eg. placing 1 bet-builder from Combo)
                    if (context.types.base.currentSelectedType === BetslipType.Combo) {
                        return this.messages.multiAccepted;
                    }

                    if (response.betslips.some((b) => b.status !== PlacementType.Succeeded)) {
                        return this.stringInterpolationPipe.transform(this.messages.singleBetsAccepted, {
                            count: response.betslips.filter((b) => b.status === PlacementType.Succeeded).length,
                            all: response.betslips.length,
                        });
                    } else {
                        return this.stringInterpolationPipe.transform(this.messages.singleAccepted, { count: response.betslips.length });
                    }
                case BetSlipType.System:
                    return this.messages.systemBetsAccepted;
            }
        }

        return '';
    }

    private *processSucceededPicks(
        placedBetslipV2: PlacedBetslip,
        requestPicks: IBetPicks,
        isTeaserBet: boolean,
    ): Iterable<ISucceededSlipPickResult> {
        const picksWithinAGroup = new Set<string>();

        if (placedBetslipV2.succeeded?.betGroups) {
            for (const group of placedBetslipV2.succeeded.betGroups.filter((t) => t.betGroupType === BetGroupType.Ogp)) {
                const groupPick = requestPicks.groups[group.groupIndex];
                this.updateGroupPickLegIds(picksWithinAGroup, groupPick);
                yield {
                    odds: OddsOperations.createOdds2(group.concludedOdds, group.oddsFormat),
                    eventName: groupPick.eventName.name,
                    marketName: '',
                    optionNames: [],
                    slipOptionType: SlipOptionType.Group,
                    pickId: groupPick.id,
                    clientPick: groupPick,
                    totalOdds: OddsOperations.createOdds3(placedBetslipV2.succeeded.totalOdds),
                };
            }
        }

        const picks = Object.values(requestPicks.picks);
        for (const option of placedBetslipV2.succeeded?.options ?? []) {
            if (picksWithinAGroup.has(option.pickId.toString())) {
                continue;
            }

            const pickId = pickIdFactory(option.pickId);
            const pick = picks.find((p) => p.id.isEqual(pickId));
            option.optionNames =
                pick instanceof BetslipV2OptionMarketXCastRacePick
                    ? pick.options.map((o) => {
                          return o.name.name;
                      })
                    : option.optionNames;
            yield {
                ...option,
                pickId,
                odds: OddsOperations.createOdds3(option.odds),
                clientPick: picks.find((p) => p.id.isEqual(pickId) || (isTeaserBet && p.id.fixtureId === pickId.fixtureId)),
                isTeaserBet,
                totalOdds: OddsOperations.createOdds3(placedBetslipV2.succeeded?.totalOdds),
            };
        }
    }

    // if we have a pick with boostedPrice that was part of a group, the bet placement request uses the original priceID instead
    // of the boosted one. When we're mapping back the response, we have to match the client/response picks by original(accepted) price
    private updateGroupPickLegIds(picksWithinAGroup: Set<string>, groupPick: BaseBetslipGroupPick): void {
        for (const leg of groupPick.getLegs()) {
            if (BetslipV2StandardPick.isPick(leg) && leg.acceptedPrice.price) {
                picksWithinAGroup.add(
                    V2OptionMarketPickId.asString(leg.id.fixtureId, leg.id.optionMarketId, leg.id.optionId, leg.acceptedPrice.price.id),
                );
            } else {
                picksWithinAGroup.add(leg.id.toString());
            }
        }
    }

    private mapSucceededSlip(
        placedBetslipV2: PlacedBetslip,
        request: PlaceBetRequest,
        requestPicks: IBetPicks,
    ): ISucceededSlipResult | ISucceededPartialSlipResult {
        if (placedBetslipV2.succeeded && !placedBetslipV2.succeeded.options) {
            return {
                status: SlipResultStatus.Success,
                betNumber: placedBetslipV2.succeeded.betNumber,
                conclusionDate: new Date(placedBetslipV2.succeeded.conclusionDate),
            };
        }

        const isTeaserBet = placedBetslipV2.succeeded!.betGroups?.some((bg) => bg.betGroupType === BetGroupType.Teaser);

        const currency = placedBetslipV2.succeeded!.stake
            ? placedBetslipV2.succeeded!.stake.currency
            : placedBetslipV2.succeeded!.totalStake?.currency;
        // When no stake per bet then use the total stake, e.g. have only one bet
        const stake = placedBetslipV2.succeeded!.stake ? placedBetslipV2.succeeded!.stake.value : placedBetslipV2.succeeded!.totalStake?.value;
        const rewardUsed =
            placedBetslipV2.succeeded!.isFreeBet ||
            !!placedBetslipV2.succeeded!.promoTokens?.length ||
            !!placedBetslipV2.succeeded!.edsPromoTokens?.length;
        const slip: ISucceededSlipResult = {
            status: SlipResultStatus.Success,
            betNumber: placedBetslipV2.succeeded!.betNumber,
            betType: placedBetslipV2.succeeded!.betType,
            currency,
            stake,
            totalStake: placedBetslipV2.succeeded!.totalStake?.value,
            picks: Array.from(this.processSucceededPicks(placedBetslipV2, requestPicks, isTeaserBet ?? false)),
            isFreebet: placedBetslipV2.succeeded!.isFreeBet ?? false,
            promoTokens: placedBetslipV2.succeeded!.promoTokens ?? [],
            boostedOdds: this.mapBoostedOdds(placedBetslipV2, request),
            edsPromoTokens: placedBetslipV2.succeeded!.edsPromoTokens ?? [],
            conclusionDate: new Date(placedBetslipV2.succeeded!.conclusionDate),
            systemBetDetails: placedBetslipV2.succeeded!.systemBetDetails,
            rewardUsed,
            systemKey: this.getSystemKey(placedBetslipV2.succeeded!.betType, placedBetslipV2.succeeded!.index, request.betSlips),
            isMultiBB: this.isMultiBB(placedBetslipV2.succeeded!, requestPicks),
            isMultiTeaser: isTeaserBet,
            betCount: 0,
            signPostings: this.getSignPostings(placedBetslipV2),
        };
        slip.betCount = getLegsCount(slip.picks.map((p) => p.clientPick).filter(isDefined));
        if (placedBetslipV2.succeeded?.totalOdds) {
            // When one of the picks is each way, SP or no price, the total odds should not be displayed
            if (!slip.picks.some((pick) => (pick.participantBetDetails ?? pick.optionBetDetails)?.betType === BetType.EachWay)) {
                // When SP/No price selected no odds available
                slip.totalOdds = OddsOperations.createOdds3(placedBetslipV2.succeeded.totalOdds);
            }
        }
        if (placedBetslipV2.succeeded?.possibleWinningsNet) {
            // If there are net winning ( this is what we show ) we set winnings.
            // Winnings maybe not available for SP or No price options
            // If no gross winnings then take the net winnings, which we show taxation equal to zero
            slip.possibleWinnings = {
                net: placedBetslipV2.succeeded.possibleWinningsNet.value,
                gross: placedBetslipV2.succeeded.possibleWinningsGross
                    ? slip.isFreebet
                        ? placedBetslipV2.succeeded.possibleWinningsGross.value - (stake ?? 0)
                        : placedBetslipV2.succeeded.possibleWinningsGross.value
                    : placedBetslipV2.succeeded.possibleWinningsNet.value,
            };
        }

        return slip;
    }

    // When the new signPostings object have no data and the edsPromoTokens tokenType is BetAndGet
    // then return edsPromoTokens[0].additionalInformation PlaceBetSignPostingData
    private getSignPostings(placedBetslipV2: PlacedBetslip): SignPost[] {
        if (placedBetslipV2.succeeded?.signPostings?.length) {
            return placedBetslipV2.succeeded.signPostings;
        }

        if (
            placedBetslipV2.succeeded?.edsPromoTokens?.length &&
            placedBetslipV2.succeeded.edsPromoTokens[0].tokenType === EdsPromoTokenType.BetAndGet
        ) {
            const placeBetSignPost = placedBetslipV2.succeeded?.edsPromoTokens[0]?.additionalInformation.informationItems.find(
                (item: KeyValueInformation) => item.key === 'PlaceBetSignPostingData',
            )?.value;

            const placeBetSignPostVersion = Number(
                placedBetslipV2.succeeded.edsPromoTokens[0].additionalInformation.informationItems.find(
                    (item: KeyValueInformation) => item.key === 'PlaceBetSignPostingVersion',
                )?.value ?? 1,
            );

            return placeBetSignPost ? [{ type: SignPostType.BetPlacement, version: placeBetSignPostVersion, data: placeBetSignPost }] : [];
        }

        return [];
    }

    private getSystemKey(betType: BetSlipType, index: number, requestBetslips: BetSlip[]): string | undefined {
        if (betType !== BetSlipType.System) {
            return;
        }

        return reconvertSystem(requestBetslips.find((b) => b.index === index)?.systemSlipDetails?.systemType || '');
    }

    private mapBetslipType(betSlipType: BetSlipType): BetslipType {
        switch (betSlipType) {
            case BetSlipType.Single:
                return BetslipType.Single;
            case BetSlipType.Combo:
                return BetslipType.Combo;
            default:
                return BetslipType.System;
        }
    }

    private mapFailedSlip(
        requestId: string,
        placedBetslipV2: PlacedBetslip,
        requestPicks: IBetPicks,
        betslipRequestContext: BetSlip,
        selectedType: BetslipType,
    ): IFailedSlipResult {
        const processErrors = this.processErrors(
            placedBetslipV2.failed,
            requestPicks,
            selectedType,
            betslipRequestContext.systemSlipDetails?.systemType,
        );

        this.logInsufficientFundIfAny(requestId, processErrors.slipErrors, processErrors.picks);

        return {
            status: SlipResultStatus.Fail,
            errors: processErrors.slipErrors,
            picks: processErrors.picks,
            overAskOffer: placedBetslipV2.failed?.overAskOffer,
            betType: betslipRequestContext.betSlipType,
            details: getFailedSlipDetails(
                betslipRequestContext.bets,
                betslipRequestContext.betGroups,
                placedBetslipV2.failed?.index,
                betslipRequestContext.betSlipType,
                requestPicks,
            ),
        };
    }

    // TODO: @Andriy logic below is shared between result page & my-bets
    private mapBoostedOdds({ succeeded }: PlacedBetslip, request: PlaceBetRequest): Odds | undefined {
        const oddsBoost = succeeded?.promoTokens?.find((token) => token.tokenType === PromoTokenType.OddsBoost);
        if (!oddsBoost?.additionalInformation?.informationItems) {
            return;
        }

        //this data (boostPercentage) isn't provided by bpos response, so we had to access it from request payload
        const oddsBoostTokenInfos = request.betSlips.find(
            (betslip) => betslip.promoTokens.find((token) => token.tokenType === PromotionTokenType.OddsBoost) && betslip.index === succeeded?.index,
        )?.promoTokens[0].additionalInformation.informationItems;

        if (!oddsBoostTokenInfos) {
            return;
        }

        const boostFactor = Number(oddsBoostTokenInfos.find((t) => t.key === RewardTokenAdditionalInfoKeys.BoostPercentage)?.value ?? 0);

        const infoKeyValue = oddsBoost.additionalInformation.informationItems.map(({ key, value }) => [key, value] as [string, string]);
        const additionalInfo = new Map<string, string>(infoKeyValue);
        const oddsFormat = additionalInfo.get('BoostedOdds.OddsFormat');
        const odds = {
            decimals: new Decimal(succeeded?.totalOdds?.european || 0),
            fractional: new Fraction(succeeded?.totalOdds?.british?.numerator || 0, succeeded?.totalOdds?.british?.denominator || 1),
            moneyline: new Decimal(succeeded?.totalOdds?.american || 0),
        };

        const format = OddsOperations.convertOddsFormat(oddsFormat as OddsFormat);
        const calculatedOdds = this.tokensCalculator.getBoostedNativeOdds2(odds, boostFactor, this.getOddsBoostFormula(oddsBoostTokenInfos), format);

        return {
            decimals: calculatedOdds?.decimals?.toNumber() ?? 0,
            fractional: {
                denominator: calculatedOdds?.fractional?.denominator?.toNumber() ?? 0,
                numerator: calculatedOdds?.fractional?.numerator?.toNumber() ?? 0,
            },
            moneyline: calculatedOdds?.moneyline?.toNumber() ?? 0,
        };
    }

    private getOddsBoostFormula(firstOddsBoostTokenInfos: KeyValueInformation[]): OddsBoostFormula {
        const oddsBoostFormula = firstOddsBoostTokenInfos.find((t) => t.key === RewardTokenAdditionalInfoKeys.OddsBoostFormulaType)?.value;

        return oddsBoostFormula as OddsBoostFormula;
    }

    private logInsufficientFundIfAny(requestId: string, slipErrors: BetPlacementError[], picks: { [pickId: string]: IFailedSlipPickResult }) {
        const allErrors = slipErrors.concat(Object.values(picks).flatMap((pick) => pick.errors));

        const hasInsufficientError = !!allErrors.find((err) =>
            [
                PlacementErrorType.InsufficientBonusMoney,
                PlacementErrorType.InsufficientBonusMoneyZeroBalance,
                PlacementErrorType.InsufficientFunMoney,
                PlacementErrorType.InsufficientMoney,
                PlacementErrorType.InsufficientMoneyZeroBalance,
                PlacementErrorType.BlockedPlayMoney,
            ].includes(err.type as PlacementErrorType),
        );
        if (hasInsufficientError) {
            this.balanceLogger.log(requestId);
        }
    }

    private isMultiBB(slip: SucceededBetslip, requestPicks: IBetPicks) {
        if (!slip) return false;

        const betGroupsLength = slip.betGroups?.length || 0;
        const betsLength = slip.bets?.length || 0;
        const totalGroupBetsLength = slip.betGroups?.flatMap((g) => g.bets).length || 0;

        return (
            betGroupsLength > 1 ||
            (betGroupsLength > 0 && betsLength > totalGroupBetsLength) ||
            (slip.betType === BetSlipType.Combo && Object.values(requestPicks.picks).some((p) => BetslipBetBuilderPick.isPick(p)))
        );
    }
}
